import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Slider,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MDButton from "components/MDButton";

export default function CaptureGridDialog({
  open,
  onClose,
  isMobile,
  captureViewportRef,
  videoScanInputRef,
  handleVideoScanFile,
  videoScanBusy,
  videoScanProgress,
  videoScanMessage,
  gridRows,
  gridCols,
  setGridRows,
  setGridCols,
  totalTiles,
  capturedCount,
  capturePercent,
  currentTileKey,
  currentRow,
  currentCol,
  capturedTiles,
  captureFeedback,
  startStitchPipeline,
  stitchJobId,
  stitchBusy,
  stitchStatus,
  stitchPipelinePercent,
  stitchPipelinePhase,
  stitchPipelineMessage,
}) {
  const liveMosaicCells = Array.from({ length: totalTiles }, (_, tileIndex) => {
    const row = Math.floor(tileIndex / gridCols);
    const col = tileIndex % gridCols;
    const key = `${row}-${col}`;
    const tile = capturedTiles?.[key];
    return { key, row, col, tile };
  });
  const uploadedTiles = liveMosaicCells.filter((cell) => cell.tile?.done).length;
  const remainingTiles = Math.max(0, totalTiles - uploadedTiles);

  return (
    <Dialog open={open} onClose={onClose} fullScreen={isMobile} fullWidth maxWidth="md">
      <DialogTitle sx={{ pr: 6 }}>
        Capture Slide (Video Scan)
        <IconButton onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 1.5, flexWrap: "wrap" }}>
          <Typography variant="caption">Rows</Typography>
          <Slider
            value={gridRows}
            onChange={(_, v) => setGridRows(Number(v))}
            min={2}
            max={10}
            step={1}
            valueLabelDisplay="auto"
            sx={{ width: 120 }}
          />
          <Typography variant="caption">Cols</Typography>
          <Slider
            value={gridCols}
            onChange={(_, v) => setGridCols(Number(v))}
            min={2}
            max={10}
            step={1}
            valueLabelDisplay="auto"
            sx={{ width: 120 }}
          />
          <Typography variant="body2" sx={{ ml: "auto" }}>
            {capturedCount}/{totalTiles} ({capturePercent}%)
          </Typography>
        </Box>

        <LinearProgress
          variant="determinate"
          value={capturePercent}
          sx={{ height: 8, borderRadius: 999, mb: 1.5 }}
        />

        <Box
          ref={captureViewportRef}
          sx={{
            position: "relative",
            width: "100%",
            borderRadius: 2,
            overflow: "hidden",
            border: "1px solid rgba(255,255,255,0.18)",
            bgcolor: "#000",
            aspectRatio: "4/3",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: `repeat(${gridCols}, minmax(0, 1fr))`,
              gridTemplateRows: `repeat(${gridRows}, minmax(0, 1fr))`,
              gap: 0.45,
              width: "100%",
              height: "100%",
              p: 0.7,
              bgcolor: "#000",
            }}
          >
            {liveMosaicCells.map((cell) => (
              <Box
                key={cell.key}
                sx={{
                  position: "relative",
                  width: "100%",
                  height: "100%",
                  minHeight: 24,
                  borderRadius: 0.6,
                  overflow: "hidden",
                  border:
                    cell.key === currentTileKey
                      ? "2px solid #00e5ff"
                      : "1px solid rgba(148,163,184,.45)",
                  bgcolor: "#000",
                }}
              >
                {cell.tile?.url ? (
                  <img
                    src={cell.tile.url}
                    alt={`Tile ${cell.row + 1}-${cell.col + 1}`}
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "rgba(226,232,240,0.75)",
                      fontSize: 11,
                      fontWeight: 700,
                    }}
                  >
                    {cell.row + 1},{cell.col + 1}
                  </Box>
                )}
                {cell.tile?.status === "uploading" ? (
                  <LinearProgress
                    variant="determinate"
                    value={Number(cell.tile.progress || 0)}
                    sx={{
                      position: "absolute",
                      left: 0,
                      right: 0,
                      bottom: 0,
                      height: 3,
                    }}
                  />
                ) : null}
              </Box>
            ))}
          </Box>
          {!videoScanBusy && uploadedTiles === 0 ? (
            <Typography
              variant="body2"
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "rgba(255,255,255,0.92)",
                fontWeight: 700,
                textAlign: "center",
                px: 2,
                pointerEvents: "none",
              }}
            >
              Start video recording scan and this black screen will fill automatically.
            </Typography>
          ) : null}
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
          Uploaded {uploadedTiles}/{totalTiles} tiles - Remaining {remainingTiles}
        </Typography>

        {captureFeedback ? (
          <Typography variant="body2" sx={{ mt: 1.2, color: "success.main", fontWeight: 600 }}>
            {captureFeedback}
          </Typography>
        ) : null}

        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.6, display: "block" }}>
          Tile {currentRow + 1},{currentCol + 1} -{" "}
          {capturedTiles?.[currentTileKey]?.status === "uploading"
            ? `Uploading ${capturedTiles?.[currentTileKey]?.progress || 0}%`
            : capturedTiles?.[currentTileKey]?.status === "error"
              ? capturedTiles?.[currentTileKey]?.error || "Upload failed"
              : capturedTiles?.[currentTileKey]?.done
                ? "Uploaded"
                : "Pending"}
        </Typography>

        {stitchJobId ? (
          <Box sx={{ mt: 0.8 }}>
            <Typography variant="caption" color="info.main" sx={{ display: "block", mb: 0.4 }}>
              Stitch job: {stitchJobId} {stitchStatus ? `(${stitchStatus})` : ""}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={stitchPipelinePercent}
              sx={{ height: 8, borderRadius: 999 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.35, display: "block" }}>
              {stitchPipelinePhase ? `${stitchPipelinePhase.toUpperCase()} - ` : ""}
              {stitchPipelineMessage || "Processing..."} ({stitchPipelinePercent}%)
            </Typography>
          </Box>
        ) : null}

        {videoScanBusy || videoScanProgress > 0 ? (
          <Box sx={{ mt: 0.8 }}>
            <Typography variant="caption" color="info.main" sx={{ display: "block", mb: 0.4 }}>
              Video auto-extraction {videoScanBusy ? "(running)" : "(done)"}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={Math.max(0, Math.min(100, Number(videoScanProgress || 0)))}
              sx={{ height: 8, borderRadius: 999 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.35, display: "block" }}>
              {videoScanMessage || "Extracting frames from slow scan video..."} ({videoScanProgress}%)
            </Typography>
          </Box>
        ) : null}

        <Box sx={{ display: "flex", justifyContent: "center", mt: 2, flexWrap: "wrap", gap: 1 }}>
          <input
            ref={videoScanInputRef}
            type="file"
            accept="video/*"
            capture="environment"
            style={{ display: "none" }}
            onChange={handleVideoScanFile}
          />
          <MDButton
            color="info"
            variant="contained"
            disabled={videoScanBusy || stitchBusy}
            onClick={() => videoScanInputRef?.current?.click()}
            sx={{
              minWidth: 230,
              minHeight: 56,
              borderRadius: 999,
              fontSize: 16,
              fontWeight: 700,
            }}
          >
            {videoScanBusy ? "Recording / Processing..." : "Start Video Recording Scan"}
          </MDButton>

          {capturedCount === totalTiles && totalTiles > 0 ? (
            <MDButton
              color="success"
              variant="contained"
              disabled={videoScanBusy || stitchBusy || !!stitchJobId}
              onClick={startStitchPipeline}
              sx={{
                minWidth: 190,
                minHeight: 56,
                borderRadius: 999,
                fontSize: 16,
                fontWeight: 700,
              }}
            >
              {stitchBusy || stitchStatus === "processing" ? (
                <CircularProgress size={22} color="inherit" />
              ) : (
                "Compress & Tile"
              )}
            </MDButton>
          ) : null}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
